import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { US_STATES_LIST } from "@/lib/states";
import { MapPin } from "lucide-react";
import { t } from "@/lib/i18n";

const PropertySetup = ({ property, onSaved }) => {
  const [editing, setEditing] = useState(!property);
  const [street, setStreet] = useState(property?.street || "");
  const [city, setCity] = useState(property?.city || "");
  const [state, setState] = useState(property?.state || "");
  const [zip, setZip] = useState(property?.zip_code || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/property", {
        street, city, state: state.toUpperCase(), zip_code: zip,
      });
      onSaved(data);
      setEditing(false);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing && property) {
    return (
      <div className="card-flat" data-testid={TESTIDS.propertyCard}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F4F7F6] flex items-center justify-center">
              <MapPin size={16} className="text-[#2F524A]" />
            </div>
            <div>
              <p className="eyebrow">{t("My Property")}</p>
              <p className="font-serif text-lg text-[#1C201F] mt-1">
                {property.street}
              </p>
              <p className="text-sm text-[#5C6661]">
                {property.city}, {property.state} {property.zip_code}
              </p>
            </div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-[#2F524A] hover:underline"
            data-testid={TESTIDS.propertyEditBtn}
          >
            {t("Edit")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={save} className="card-flat space-y-4">
      <p className="eyebrow">{t("Set up your property")}</p>
      <p className="text-sm text-[#5C6661]">
        {t("Add the address of the home you’re testing. This creates your permanent Radon Record.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
        <div className="sm:col-span-6">
          <label className="eyebrow block mb-2">{t("Street")}</label>
          <input
            className="input-field"
            required
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder={t("123 Main St")}
            data-testid={TESTIDS.propertyStreetInput}
          />
        </div>
        <div className="sm:col-span-3">
          <label className="eyebrow block mb-2">{t("City")}</label>
          <input
            className="input-field"
            required
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("Pittsburgh")}
            data-testid={TESTIDS.propertyCityInput}
          />
        </div>
        <div className="sm:col-span-1">
          <label className="eyebrow block mb-2">{t("State")}</label>
          <select
            className="input-field"
            required
            value={state}
            onChange={(e) => setState(e.target.value)}
            data-testid={TESTIDS.propertyStateInput}
          >
            <option value="" disabled>—</option>
            {US_STATES_LIST.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow block mb-2">{t("ZIP")}</label>
          <input
            className="input-field"
            required
            inputMode="numeric"
            pattern="\d{5}"
            maxLength={5}
            minLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="15213"
            data-testid={TESTIDS.propertyZipInput}
          />
        </div>
      </div>
      {error && (
        <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-3">
        {property && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="btn-secondary !py-2 !px-4 text-sm"
          >
            {t("Cancel")}
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="btn-primary !py-2 !px-5 text-sm"
          data-testid={TESTIDS.propertySaveBtn}
        >
          {saving ? t("Saving…") : property ? t("Save changes") : t("Save property")}
        </button>
      </div>
    </form>
  );
};

export default PropertySetup;
